import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { facilityMetricsQuery } from '@b-stock/fe-core/bstock-next/queries'

import { FeatureFlags } from '@/helpers/featureFlags'

/**
 * Convert milliseconds to day range for shipping time
 * @param ms - Milliseconds
 * @returns Object with min/max days or null if less than 1 day
 */
const formatPaidToShipDays = (
  ms: number
): { min: number; max: number } | null => {
  const msPerDay = 1000 * 60 * 60 * 24
  if (ms < msPerDay) return null
  return { min: Math.floor(ms / msPerDay), max: Math.ceil(ms / msPerDay) }
}

export interface P2SData {
  avgPaidToShipMs: number | null
  avgPaidToShipDays: { min: number; max: number } | null
  avgPaidToShipText: string | null
  isLoading: boolean
}

/**
 * Format P2S time for UI display
 * @param avgPaidToShipMs - Raw milliseconds
 * @param intl - Intl instance for formatting
 * @returns Formatted text like "3-4 days" or null if invalid
 */
const formatP2SText = (
  avgPaidToShipMs: number | null,
  intl: IntlShape
): string | null => {
  if (avgPaidToShipMs === null || avgPaidToShipMs < 0) return null

  const days = formatPaidToShipDays(avgPaidToShipMs)
  return days === null
    ? intl.formatMessage({ id: 'Listing.paidToShippedTime.lessThanOneDay' })
    : intl.formatMessage(
        { id: 'Listing.paidToShippedTime.days' },
        { min: days.min, max: days.max }
      )
}

/**
 * Centralized hook for fetching and formatting P2S (Paid-to-Shipped) data
 * Provides raw data, day range, and formatted text for UI display
 *
 * @param locationOriginalId - The location ID to fetch metrics for
 * @returns Object containing raw P2S data, day range, formatted text, and loading state
 */
export const useListingP2SData = (
  locationOriginalId?: string | null
): P2SData => {
  const p2sVariant = useFlag(FeatureFlags.ENABLE_LDP_P2S_METRICS_AB)
  const intl = useIntl()
  const accessToken = useAccessToken({ required: false })

  // Fetch facility metrics only if user is authenticated, feature flag is enabled, and we have a location
  const { data: facilityMetrics, isLoading } = facilityMetricsQuery.useQuery(
    locationOriginalId ?? '',
    {
      enabled: !!accessToken && p2sVariant !== 'off' && !!locationOriginalId,
    }
  )

  const avgPaidToShipMs = facilityMetrics?.avgPaidToShipMs ?? null
  const avgPaidToShipDays =
    avgPaidToShipMs !== null && avgPaidToShipMs >= 0
      ? formatPaidToShipDays(avgPaidToShipMs)
      : null
  const avgPaidToShipText = formatP2SText(avgPaidToShipMs, intl)

  return {
    avgPaidToShipMs,
    avgPaidToShipDays,
    avgPaidToShipText,
    isLoading,
  }
}
